import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { TouchableButton } from '@components/common/Touchable';
import { HoldTimerIcon } from '@assets/images/HoldIcons/holdTimer.jsx';
import { ReactComponent as HoldUndeleteIcon } from '@assets/images/HoldIcons/holdUndelete.svg';
import { ReactComponent as Delete } from '@assets/images/delete.svg';
import { ReactComponent as NewDeleteIcon } from '@assets/images/HoldIcons/newCrossIcon.svg';
import cn from 'classnames';
import { getHoldTimerState } from '@utility/helperFunction';
import styles from './holdController.module.scss';

// ── Module-level constants ─────────────────────────────────────────────────
const MINUTE_VALUES = Array.from({ length: 60 }, (_, i) => i + 1);
const RESET_TIMER = { timeStr: '00:00', isPositive: true };
// Natural (unclamped) height of the dropdown (header + grid + padding) and the
// minimum height it's still usable at once shrunk to fit the viewport.
const DROPDOWN_HEIGHT = 190;
const DROPDOWN_MIN_HEIGHT = 82;
const VIEWPORT_MARGIN = 12;

// ── Isolated timer span — only this re-renders every second ────────────────
const HoldTimerText = React.memo(({ sendDate, holdDeleted, serverAppTheme }) => {
  const [timerState, setTimerState] = useState(
    () => (sendDate ? getHoldTimerState(sendDate) : RESET_TIMER) || RESET_TIMER
  );

  useEffect(() => {
    if (!sendDate) {
      setTimerState(RESET_TIMER);
      return;
    }
    const tick = () => setTimerState(getHoldTimerState(sendDate) || RESET_TIMER);
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [sendDate]);

  const { timeStr, isPositive } = timerState;
  const color = holdDeleted || !isPositive
    ? serverAppTheme?.holdNegative
    : serverAppTheme?.holdPositive;

  return (
    <span
      className={cn('fs_14 mr-2 fw-600', { [styles.timeDeleted]: holdDeleted })}
      style={{ color }}
    >
      {timeStr}
    </span>
  );
});

// ── Component ──────────────────────────────────────────────────────────────
const HoldController = ({
  isEditMode,
  hasHoldApplied,
  holdMinutes,
  setHoldMinutes,
  holdDeleted,
  setHoldDeleted,
  sendDate,
  serverAppTheme,
  dropdownOpensDown,
  isFromHoldScreen,
}) => {
  // ── State ──────────────────────────────────────────────────────────────
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({});

  // ── Refs ───────────────────────────────────────────────────────────────
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  // ── Effects ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!showDropdown) return;
    const onClickOutside = (e) => {
      const inWrapper = wrapperRef.current?.contains(e.target);
      const inDropdown = dropdownRef.current?.contains(e.target);
      if (!inWrapper && !inDropdown) setShowDropdown(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [showDropdown]);

  // ── Handlers ───────────────────────────────────────────────────────────
  const handleToggleDropdown = useCallback(() => {
    setShowDropdown(prev => {
      if (!prev && triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN;
        const spaceAbove = rect.top - VIEWPORT_MARGIN;

        // Dynamically adjust direction if viewport constraints cause cropping
        let opensDown = dropdownOpensDown;
        if (opensDown && spaceBelow < DROPDOWN_HEIGHT && spaceAbove > spaceBelow) {
          opensDown = false;
        } else if (!opensDown && spaceAbove < DROPDOWN_HEIGHT && spaceBelow > spaceAbove) {
          opensDown = true;
        }

        const available = opensDown ? spaceBelow : spaceAbove;
        const maxHeight = Math.max(DROPDOWN_MIN_HEIGHT, Math.min(DROPDOWN_HEIGHT, available));

        setDropdownPos(
          opensDown
            ? { top: rect.bottom + 6, right: window.innerWidth - rect.right, maxHeight }
            : { bottom: window.innerHeight - rect.top + 6, right: window.innerWidth - rect.right, maxHeight }
        );
      }
      return !prev;
    });
  }, [dropdownOpensDown]);

  const handleSelectMinute = useCallback((e) => {
    setHoldMinutes(Number(e.currentTarget.dataset.val));
    setShowDropdown(false);
  }, [setHoldMinutes]);

  const handleDeleteHold = useCallback(() => setHoldDeleted(true), [setHoldDeleted]);
  const handleUndeleteHold = useCallback(() => setHoldDeleted(false), [setHoldDeleted]);
  const handleClearMinutes = useCallback((e) => {
    e.preventDefault();
    setHoldMinutes(0);
  }, [setHoldMinutes]);

  // ── Memoized values ────────────────────────────────────────────────────
  const triggerLabel = useMemo(
    () => (holdMinutes === 0 ? 'Set time' : String(holdMinutes)),
    [holdMinutes]
  );

  // ── Dropdown mode ──────────────────────────────────────────────────────
  if (!isEditMode || !hasHoldApplied) {
    return (
      <div className="w-100" ref={wrapperRef}>
        <button
          ref={triggerRef}
          type="button"
          className={cn('d-flex align-items-center justify-content-between w-100', styles.trigger)}
          onClick={handleToggleDropdown}
          style={{ paddingRight: holdMinutes > 0 ? '6px' : '' }}
        >
          <span className={styles.triggerLabel}>{triggerLabel}</span>
          <span className={cn(styles.triggerArrow, { [styles.triggerArrowOpen]: showDropdown })} />
          {holdMinutes > 0 && (
            <TouchableButton
              className="d-flex align-items-center justify-content-center ml-0 pr-0 border-0 bg-transparent"
              onClick={handleClearMinutes}
            >
              <NewDeleteIcon width={28} height={28} style={{ color: serverAppTheme?.primaryBtn }} />
            </TouchableButton>
          )}
        </button>
        {showDropdown && (
          <div
            ref={dropdownRef}
            className={styles.dropdown}
            style={dropdownPos}
          >
            <div className={styles.dropdownHeader}>Select Minutes</div>
            <div className={styles.grid}>
              {MINUTE_VALUES.map(val => (
                <button
                  key={val}
                  type="button"
                  data-val={val}
                  className={cn(styles.item, { [styles.itemSelected]: holdMinutes === val })}
                  onClick={handleSelectMinute}
                >
                  {val}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Timer mode ─────────────────────────────────────────────────────────
  return (
    <div className="d-flex align-items-center w-100">
      <div
        className={cn(styles.holdTimer, styles.holdTimerActive, { [styles.holdTimerBorderDefault]: !isFromHoldScreen })}
        style={{ backgroundColor: isFromHoldScreen ? serverAppTheme?.OFF_Premise_Btn : undefined }}
      >
        <HoldTimerIcon
          width={20}
          height={20}
          className={styles.timerIcon}
          fill='#000'
        />
        <HoldTimerText
          sendDate={sendDate}
          holdDeleted={holdDeleted}
          serverAppTheme={serverAppTheme}
        />
        {holdDeleted ? (
          <TouchableButton
            className={cn('border-0 rounded d-flex justify-content-center align-items-center', styles.action)}
            style={{ backgroundColor: serverAppTheme?.primaryBtn }}
            onClick={handleUndeleteHold}
          >
            <HoldUndeleteIcon width={20} height={20} />
          </TouchableButton>
        ) : (
          <TouchableButton
            className={cn('border-0 rounded d-flex justify-content-center align-items-center', styles.action)}
            style={{ backgroundColor: serverAppTheme?.primaryBtn }}
            onClick={handleDeleteHold}
          >
            <Delete fill={serverAppTheme?.btn_font_color} width={16} height={16} />
          </TouchableButton>
        )}
      </div>
    </div>
  );
};

export default React.memo(HoldController);
